/**
 * KycCompanyDetailsForm — captures the fields the bulk KYC trigger needs
 * (Company PAN, GSTIN, date of incorporation, founder DIN). Persists to the
 * startup profile so admins can then click "Run all company checks" with
 * every input already populated.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button, Card } from "../common/Primitives";

const FIELDS = [
  { key: "cin",                  label: "CIN",                  ph: "U74999DL2021PTC001234", uppercase: true, maxLength: 21 },
  { key: "llpin",                label: "LLPIN",                ph: "AAA-1234 (LLPs only)", uppercase: true, maxLength: 8 },
  { key: "pan",                  label: "Company PAN",          ph: "AAACU5052B", uppercase: true, maxLength: 10 },
  { key: "gstin",                label: "GSTIN",                ph: "09AACCU5052B1ZM", uppercase: true, maxLength: 15 },
  { key: "date_of_incorporation", label: "Date of Incorporation", ph: "DD/MM/YYYY", maxLength: 10 },
  { key: "founder_din",          label: "Founder DIN",          ph: "8-digit DIN", maxLength: 8 },
];

export default function KycCompanyDetailsForm() {
  const [values, setValues] = useState({ cin: "", llpin: "", pan: "", gstin: "", date_of_incorporation: "", founder_din: "" });
  const [initial, setInitial] = useState(values);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/startup/me");
      const s = data?.startup || {};
      const next = {
        cin: s.cin || "",
        llpin: s.llpin || "",
        pan: s.pan || "",
        gstin: s.gstin || "",
        date_of_incorporation: s.date_of_incorporation || "",
        founder_din: s.founder_din || "",
      };
      setValues(next);
      setInitial(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = FIELDS.some((f) => (values[f.key] || "") !== (initial[f.key] || ""));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const f of FIELDS) payload[f.key] = (values[f.key] || "").trim() || null;
      await api.put("/startup/profile", payload);
      toast.success("Company details saved.");
      setInitial(values);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  return (
    <Card testId="kyc-company-details-form" className="max-w-2xl">
      <div className="flex items-center gap-3 mb-3">
        <Building className="text-gold" size={20} />
        <div className="flex-1">
          <div className="font-heading text-ink text-lg">Company & Director Details</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
            Feeds the admin bulk verification trigger
          </div>
        </div>
      </div>
      <p className="text-xs text-ink-soft mb-3">
        Complete these so admins can run all company checks (CIN + GSTIN + Company PAN + LLPIN + DIN)
        in a single click. Left blank fields are skipped during bulk verification.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="block text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1">{f.label}</span>
            <input
              value={values[f.key]}
              onChange={(e) => {
                const raw = e.target.value;
                const next = f.uppercase ? raw.toUpperCase() : raw;
                setValues((v) => ({ ...v, [f.key]: next.slice(0, f.maxLength) }));
              }}
              placeholder={f.ph}
              className="w-full px-3 py-2 rounded border border-ink-line/40 bg-white text-sm"
              data-testid={`kyc-company-${f.key}`}
            />
          </label>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || busy} data-testid="kyc-company-save-btn">
          <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : dirty ? "Save details" : "Saved"}
        </Button>
        {!dirty && FIELDS.every((f) => values[f.key]) && (
          <span className="text-xs text-emerald-700 font-heading">All fields populated ✓</span>
        )}
      </div>
    </Card>
  );
}
